import { TypedDocumentNode, gql } from '@apollo/client';
import { isFuture } from 'date-fns';
import { FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { PlayerFieldStatus } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LinkOther } from '@sorare/core/src/atoms/navigation/Box';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { PlayerGameScoreScore } from '@sorare/core/src/components/scoring/PlayerGameScoreScore';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { isType } from '@sorare/core/src/lib/gql';
import { laptopAndBelow } from '@sorare/core/src/style/mediaQuery';

import { Game } from 'components/game/Game';
import { HorizontalGame } from 'components/game/HorizontalGame';
import { SubIcon } from 'components/stats/SubIcon';
import { isGameStarted } from 'lib/so5';

import { MatchCenterGame_gameWithPlayerGameScoresPreview } from './__generated__/index.graphql';

const GameWrapper = styled.div`
  border-radius: var(--double-unit);
  background: var(--c-nd-50);
`;
const PlayerButton = styled(ButtonBase)`
  position: relative;
  isolation: isolate;
  transition: opacity 0.2s ease-in-out;
  &:hover {
    opacity: 0.5;
  }
`;
const PlayerAvatar = styled.div`
  display: flex;
  justify-content: center;
  width: var(--quadruple-unit);
  height: var(--quadruple-unit);
  border-radius: 50%;
  overflow: hidden;
  background-color: var(--c-nd-150);

  img {
    object-fit: cover;
    object-position: center;
  }
`;
const PlayerIcon = styled.div`
  position: absolute;
  top: 0;
  left: calc(-1 * var(--half-unit));
  font-size: 0;
`;
const PlayerGameScores = styled(Horizontal)`
  align-items: start;

  @media ${laptopAndBelow} {
    padding-bottom: var(--double-unit);
  }
`;
const StyledLinkOther = styled(LinkOther)`
  z-index: 0;
`;
const More = styled(Horizontal).attrs({ center: true })`
  width: var(--quadruple-unit);
  height: var(--quadruple-unit);
  border-radius: 50%;
  background: var(--c-nd-50);

  transition: opacity 0.2s ease-in-out;
  &[type='button']:hover {
    opacity: 0.5;
  }
`;

type Props = {
  onClickPlayerGameScore?: (
    playerGameScoreId: string,
    playerSlug: string
  ) => void;
  onClickGame?: (gameId: string, myPlayerSlugs?: string[]) => void;
  gameWithPlayerGameScores: MatchCenterGame_gameWithPlayerGameScoresPreview;
};

export const MatchCenterGame = ({
  onClickGame,
  onClickPlayerGameScore,
  gameWithPlayerGameScores,
}: Props) => {
  const isLaptopAndAbove = useIsLaptop();
  const { game, playerGameScores, totalCount } = gameWithPlayerGameScores;
  const myPlayerSlugs = playerGameScores?.map(
    ({ anyPlayer }) => anyPlayer.slug
  );

  const pgsPreview = (
    <PlayerGameScores center>
      {playerGameScores.map(playerGameScore => {
        const footballPlayerGameStats = isType(
          playerGameScore.anyPlayerGameStats,
          'PlayerGameStats'
        )
          ? playerGameScore.anyPlayerGameStats
          : null;

        const substitutedIn =
          footballPlayerGameStats?.fieldStatus === PlayerFieldStatus.ON_FIELD &&
          !footballPlayerGameStats.gameStarted;
        const substitutedOut =
          footballPlayerGameStats?.fieldStatus ===
          PlayerFieldStatus.SUBSTITUTED;

        return (
          <StyledLinkOther key={playerGameScore.id}>
            <PlayerButton
              onClick={() =>
                onClickPlayerGameScore?.(
                  playerGameScore.id,
                  playerGameScore.anyPlayer.slug
                )
              }
            >
              {(substitutedIn || substitutedOut) && (
                <PlayerIcon>
                  <SubIcon type={substitutedIn ? 'sub_in' : 'sub_out'} />
                </PlayerIcon>
              )}
              <Vertical gap={0.5}>
                <PlayerAvatar>
                  <img
                    src={playerGameScore.anyPlayer.avatarPictureUrl}
                    alt=""
                    width="24"
                  />
                </PlayerAvatar>
                {isGameStarted(game.status) && (
                  <PlayerGameScoreScore
                    playerGameScore={playerGameScore}
                    shape="TEXT"
                  />
                )}
              </Vertical>
            </PlayerButton>
          </StyledLinkOther>
        );
      })}
      {totalCount > playerGameScores.length && (
        <StyledLinkOther>
          <More
            {...(!isFuture(game.date)
              ? { as: ButtonBase, onClick: () => onClickGame?.(game.id) }
              : {})}
          >
            <LabelS bold color="var(--c-nd-600)">
              <FormattedNumber
                value={totalCount - playerGameScores.length}
                signDisplay="always"
              />
            </LabelS>
          </More>
        </StyledLinkOther>
      )}
    </PlayerGameScores>
  );

  return isLaptopAndAbove ? (
    <HorizontalGame
      key={game.id}
      game={game}
      onClick={() => onClickGame?.(game.id, myPlayerSlugs)}
      footer={pgsPreview}
    />
  ) : (
    <GameWrapper key={game.id}>
      <Game
        game={game}
        transparent
        footer={pgsPreview}
        onClick={() => onClickGame?.(game.id, myPlayerSlugs)}
      />
    </GameWrapper>
  );
};

MatchCenterGame.fragments = {
  gameWithPlayerGameScoresPreview: gql`
    fragment MatchCenterGame_gameWithPlayerGameScoresPreview on GameWithPlayerGameScoresPreview {
      game {
        id
        date
        status: statusTyped
        ...HorizontalGame_anyGameInterface
        ...Game_anyGameInterface
      }
      playerGameScores {
        id
        anyPlayer {
          slug
          avatarPictureUrl
        }
        anyPlayerGameStats {
          id
          ... on PlayerGameStats {
            id
            fieldStatus
            gameStarted
          }
        }
        ...PlayerGameScoreScore_playerGameScoreInterface
      }
      totalCount
    }
    ${Game.fragments.anyGameInterface}
    ${HorizontalGame.fragments.anyGameInterface}
    ${PlayerGameScoreScore.fragments.playerGameScoreInterface}
  ` as TypedDocumentNode<MatchCenterGame_gameWithPlayerGameScoresPreview>,
};
